import React from "react";

import { ChevronLeftIcon, ChevronRightIcon } from "shared/ui/icons";

import { Arrow, Bar, Compact, Controls, Gap, PageButton, Summary } from "./styles";

interface PaginationProps {
    // Страница нумеруется с единицы — так же, как она подписана в интерфейсе.
    page: number;
    pageSize: number;
    total: number;
    onChange: (page: number) => void;
    className?: string;
}

const WINDOW = 1;

/*
* Номера страниц: края, окрестность текущей и многоточия между ними. Полный ряд рисовать
* нельзя — меню на 800 позиций в стопе даёт 40 кнопок, которые не помещаются в подвал.
* */
const buildPages = (page: number, pageCount: number): (number | "gap")[] => {
    if (pageCount <= 7) {
        return Array.from({ length: pageCount }, (_item, index) => index + 1);
    }

    const shown = new Set<number>([1, pageCount]);

    for (let candidate = page - WINDOW; candidate <= page + WINDOW; candidate++) {
        if (candidate >= 1 && candidate <= pageCount) shown.add(candidate);
    }

    const sorted = Array.from(shown).sort((left, right) => left - right);

    return sorted.reduce<(number | "gap")[]>((result, current, index) => {
        if (index > 0 && current - sorted[index - 1] > 1) result.push("gap");
        result.push(current);
        return result;
    }, []);
};

export const Pagination = ({ page, pageSize, total, onChange, className }: PaginationProps) => {
    const pageCount = Math.max(1, Math.ceil(total / pageSize));
    const firstOnPage = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const lastOnPage = Math.min(page * pageSize, total);

    return (
        <Bar className={className}>
            <Summary>
                {firstOnPage}–{lastOnPage} из {total}
            </Summary>

            <Controls>
                <Arrow
                    type="button"
                    aria-label="Предыдущая страница"
                    disabled={page <= 1}
                    onClick={() => onChange(page - 1)}
                >
                    <ChevronLeftIcon />
                </Arrow>

                {buildPages(page, pageCount).map((item, index) =>
                    item === "gap" ? (
                        <Gap key={`gap-${index}`}>…</Gap>
                    ) : (
                        <PageButton
                            key={item}
                            type="button"
                            $active={item === page}
                            aria-current={item === page}
                            onClick={() => onChange(item)}
                        >
                            {item}
                        </PageButton>
                    )
                )}

                <Compact>
                    {page} / {pageCount}
                </Compact>

                <Arrow
                    type="button"
                    aria-label="Следующая страница"
                    disabled={page >= pageCount}
                    onClick={() => onChange(page + 1)}
                >
                    <ChevronRightIcon />
                </Arrow>
            </Controls>
        </Bar>
    );
};
